import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
    addDoc,
    collection,
    doc,
    getDoc,
    onSnapshot,
    query,
    serverTimestamp,
    orderBy,
    limit,
    updateDoc,
    increment,
} from 'firebase/firestore';
import FollowButton from './FollowButton';
import TipModal from './TipModal';
import { getUserById } from '../services/userService';
import useSongAutoPlay from '../hooks/useSongAutoPlay';
import { getBadge } from '../services/badgeService';

// eslint-disable-next-line no-unused-vars
const formatCurrency = (n) => {
    try {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(Number(n || 0));
    } catch {
        return `₹${Number(n || 0).toLocaleString('en-IN')}`;
    }
};

const formatCount = (n) => Number(n || 0).toLocaleString();

const recordedSongPlays = new Set();

// eslint-disable-next-line no-unused-vars
const getBadgeForTotal = (total) => {
    const t = Number(total || 0);
    if (t >= 20) return { emoji: '👑', title: 'Crown' };
    if (t >= 10) return { emoji: '💎', title: 'Diamond' };
    if (t >= 5) return { emoji: '🥇', title: 'Gold' };
    if (t >= 3) return { emoji: '🥈', title: 'Silver' };
    if (t >= 1) return { emoji: '🥉', title: 'Bronze' };
    return null;
};

const SongCard = ({ song, showTipButton = false, onActiveChange, feedHeader = false }) => {
    const navigate = useNavigate();
    const cardRef = useRef(null);
    const recordingRef = useRef(false);
    const supportersSectionId = useMemo(() => `supporters-${song.id}`, [song.id]);
    const [artist, setArtist] = useState(null);
    const [transactions, setTransactions] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [recentTips, setRecentTips] = useState([]);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [quickTip, setQuickTip] = useState(null);
    const [deviceSeed] = useState(() => {
        const key = 'hubtuneQuickTipDeviceSeed';
        const saved = window.localStorage.getItem(key);
        if (saved) return Number(saved);
        const seed = Math.floor(Math.random() * 1000000000);
        window.localStorage.setItem(key, String(seed));
        return seed;
    });

    const realArtistId = song.artistId;
    const playCount = useMemo(
        () => Number(song.playCount ?? song.plays ?? song.totalPlays ?? 0),
        [song.playCount, song.plays, song.totalPlays]
    );

    const { isPlaying, play, pause } = useSongAutoPlay(cardRef, song);
    const quickTipPresets = useMemo(() => {
        const messages = [
            ['Say Awesome 😎', 5],
            ['Masterstroke 🔥', 10],
            ['Get tea ☕', 10],
            ['This is a vibe 🎶', 5],
            ['Loved it ❤️', 10],
            ['Pure magic ✨', 5],
            ['On repeat 🔁', 10],
            ['What a tune 🎧', 5],
            ['You nailed it 👏', 10],
            ['Mood booster 🌈', 5],
            ['Take a bow 🎤', 10],
            ['Deserves love 💖', 5],
            ['Too good 🚀', 10],
            ['Melody goals 🎼', 5],
            ['Big respect 🙌', 10],
            ['Vibe unlocked 🔓', 5],
            ['Keep shining 🌟', 10],
            ['Tea break ☕', 5],
            ['Fresh talent 💫', 10],
            ['Headphones on 🎵', 5],
        ];
        const songKey = String(song.id || song.title || '');
        const songHash = Array.from(songKey).reduce((sum, char) => sum + char.charCodeAt(0), 0);
        const start = Math.abs((deviceSeed + songHash * 31) % messages.length);
        return [0, 1, 2].map((offset) => {
            const [message, amount] = messages[(start + offset * 7) % messages.length];
            return { message, amount };
        });
    }, [deviceSeed, song.id, song.title]);

    useEffect(() => {
        if (!realArtistId) {
            setArtist(null);
            return;
        }
        async function fetchArtist() {
            try {
                const ref = doc(db, "users", realArtistId);
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const data = snap.data();
                    setArtist({ id: snap.id, ...data });
                } else {
                    console.warn('⚠️ Artist document not found:', realArtistId);
                    setArtist(null);
                }
            } catch (err) {
                console.error('❌ Error fetching artist:', err);
                setArtist(null);
            }
        }
        fetchArtist();
    }, [realArtistId]);
    


    useEffect(() => {
        if (!cardRef.current || !onActiveChange) return;
        const node = cardRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
                        onActiveChange(song);
                    }
                });
            },
            { threshold: [0.7], rootMargin: '0px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [onActiveChange, song]);

    // For SongDetail page (when showTipButton is true but no onActiveChange), 
    // automatically set this as the active song
    useEffect(() => {
        if (showTipButton && !onActiveChange && song) {
            // This is SongDetail page, handle it specially
            // For now, we'll let SongCard handle its own modal
        }
    }, [showTipButton, onActiveChange, song]);

    useEffect(() => {
        if (!song.id || !realArtistId) {
            setTransactions([]);
            return () => {};
        }

        const handleSnapshot = (snap) => {
            // The webhook writes only confirmed tips to this subcollection.
            // Reading it avoids transient transaction status snapshots hiding a tip.
            const list = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((tip) => String(tip.songId || '') === String(song.id));
            // Sort by createdAt (newest first)
            list.sort((a, b) => {
                const aStamp = a.createdAt || a.timestamp;
                const bStamp = b.createdAt || b.timestamp;
                const aTime = aStamp?.toDate
                    ? aStamp.toDate().getTime()
                    : aStamp?.seconds
                    ? aStamp.seconds * 1000
                    : aStamp?.toMillis
                    ? aStamp.toMillis()
                    : 0;
                const bTime = bStamp?.toDate
                    ? bStamp.toDate().getTime()
                    : bStamp?.seconds
                    ? bStamp.seconds * 1000
                    : bStamp?.toMillis
                    ? bStamp.toMillis()
                    : 0;
                return bTime - aTime;
            });
            setTransactions(list);
        };

        const qTx = query(
            collection(db, 'users', realArtistId, 'recentTips'),
            orderBy('createdAt', 'desc'),
            limit(20)
        );
        
        const unsub = onSnapshot(
            qTx,
            handleSnapshot,
            (err) => {
                console.error('Transactions snapshot error', err);
                setTransactions([]);
            }
        );

        return () => unsub();
    }, [song.id, realArtistId]);

    useEffect(() => {
        // Show the three most recent confirmed tips.
        // Show immediately even without user data, then enrich later
        if (transactions.length === 0) {
            setRecentTips([]);
            return;
        }

        const latest = transactions.slice(0, 3);
        
        // First, show transactions immediately (even without user data)
        setRecentTips(latest.map(tx => ({ ...tx, supporterUser: null, badgeInfo: null })));

        // Then enrich with user data asynchronously
        let cancelled = false;
        (async () => {
            try {
                const enriched = await Promise.all(
                    latest.map(async (tx) => {
                                let supporterUser = null;
                                let badgeInfo = tx.badgeType ? {
                                    badgeType: tx.badgeType,
                                    level: tx.badgeLevel,
                                    badgeTitle: tx.badgeTitle,
                                    tipCount: tx.badgeTipCount,
                                } : null;
                                if (tx.senderId && !cancelled) {
                                    try {
                                        supporterUser = await getUserById(tx.senderId);
                                        // Get badge for this supporter-artist pair
                                        // Add retry mechanism for newly confirmed transactions
                                        if (!badgeInfo && realArtistId && !cancelled) {
                                            try {
                                                badgeInfo = await getBadge(tx.senderId, realArtistId);
                                                // If badge not found and transaction is recently confirmed, retry after delay
                                                if (!badgeInfo && tx.status === 'confirmed' && !cancelled) {
                                                    // Wait a bit for badge update to complete, then retry
                                                    await new Promise(resolve => setTimeout(resolve, 500));
                                                    if (!cancelled) {
                                                        badgeInfo = await getBadge(tx.senderId, realArtistId);
                                                    }
                                                }
                                            } catch {}
                                        }
                                    } catch {}
                                }
                        return { ...tx, supporterUser, badgeInfo };
                    })
                );
                if (!cancelled) {
                    setRecentTips(enriched);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error loading recent tips', err);
                    // Keep showing transactions even if enrichment fails
                }
            }
        })();
        return () => { cancelled = true; };
    }, [transactions, realArtistId]);

    useEffect(() => {
        if (!song.songUrl || !song.id) return;
        if (isPlaying && !recordingRef.current && !recordedSongPlays.has(song.id)) {
            recordingRef.current = true;
            recordedSongPlays.add(song.id);
            (async () => {
                try {
                    await addDoc(collection(db, 'songs', song.id, 'plays'), {
                        userId: auth.currentUser?.uid || null,
                        playedAt: serverTimestamp(),
                    }).catch((err) => {
                        console.warn('Failed to record play audit', err);
                    });

                    const songRef = doc(db, 'songs', song.id);
                    await updateDoc(songRef, {
                        playCount: increment(1),
                        plays: increment(1),
                    });
                } catch (err) {
                    recordedSongPlays.delete(song.id);
                    recordingRef.current = false;
                    console.error('Failed to increment play count', err);
                }
            })();
        }
    }, [isPlaying, song.id, song.songUrl]);

    const handlePlayClick = async () => {
        if (!song.songUrl) {
            alert('Song URL missing for this track.');
            return;
        }
        try {
            if (isPlaying) {
                pause();
            } else {
                await play();
            }
        } catch (err) {
            console.error('Failed to play audio', err);
            alert('Unable to play this song. Please try again.');
        }
    };

    const handleSupportClick = () => {
        if (!auth.currentUser) {
            alert('Please sign in to tip');
            return;
        }
        if (!realArtistId || realArtistId.trim().length === 0) {
            alert('Error: Cannot determine the artist for this song. Please contact support.');
            return;
        }
        console.groupCollapsed('[HubTune Tip] Standard tip opened');
        console.table({
            songId: song.id,
            songTitle: song.title || song.name || '',
            artistId: realArtistId,
            artistName: artist?.name || artist?.displayName || song.artist || '',
            listenerUid: auth.currentUser.uid,
        });
        console.groupEnd();
        setShowSupportModal(true);
    };

    const handleQuickTip = (preset) => {
        if (!auth.currentUser) {
            alert('Please sign in to tip');
            return;
        }
        if (!realArtistId) {
            alert('Error: Cannot determine the artist for this song.');
            return;
        }
        console.groupCollapsed('[HubTune Tip] Quick tip selected');
        console.table({
            songId: song.id,
            songTitle: song.title || song.name || '',
            artistId: realArtistId,
            artistName: artist?.name || artist?.displayName || song.artist || '',
            listenerUid: auth.currentUser.uid,
            amount: preset.amount,
            message: preset.message,
        });
        console.groupEnd();
        setQuickTip(preset);
        setShowSupportModal(true);
    };

    const isOwnSong = Boolean(realArtistId && auth.currentUser?.uid === realArtistId);

    return (
        <section
            ref={cardRef}
            onPointerDown={() => onActiveChange?.(song)}
            className="song-card-shell h-screen snap-start snap-mandatory flex flex-col bg-black overflow-hidden"
        >
            {/* Main Content Area */}
            <div className={`song-card-content flex-1 flex flex-col px-4 ${feedHeader ? 'pt-28' : 'pt-20'} pb-24 overflow-y-auto`}>
                <div className="song-card-top flex items-start gap-4 w-full">
                    {/* Large Circular Cover Image on Left with Follow Button below */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-3">
                        <button
                            type="button"
                            onClick={() => realArtistId && navigate(`/profile/${realArtistId}`)}
                            className="w-24 h-24 rounded-full overflow-hidden bg-gray-800 ring-2 ring-gray-700/50 cursor-pointer hover:ring-red-500 transition"
                            aria-label="Open artist profile"
                        >
                            {artist?.photoURL ? (
                                <img
                                    src={artist.photoURL}
                                    alt={artist?.name || artist?.displayName || 'Artist'}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-4xl font-bold bg-gradient-to-br from-gray-800 to-gray-900 text-white">
                                    {(artist?.name || artist?.displayName || song.artist || 'A').charAt(0)}
                                </div>
                            )}
                        </button>
                        {/* Follow Button directly below profile picture */}
                        {realArtistId && !isOwnSong && (
                            <div className="w-24 flex justify-center">
                                <div className="w-full">
                                    <FollowButton targetUid={realArtistId} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Left Side: Artist Name, Song Title, Play Count */}
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={() => realArtistId && navigate(`/profile/${realArtistId}`)}
                            className="text-left font-bold text-white text-xl leading-tight hover:text-red-400 transition truncate"
                        >
                            {artist?.name || artist?.displayName || song.artist || 'Unknown Artist'}
                        </button>
                        <div className="text-white/80 text-base leading-tight">
                            {song.title || song.name || 'Untitled'}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-4 h-4 text-white/70"
                            >
                                <path d="M8 5v14l11-7z" />
                            </svg>
                            <span className="text-white/70 text-sm">{formatCount(playCount)}</span>
                        </div>
                    </div>

                    {/* Right Side: Stacked Icons (Play, Comment, Crown) */}
                    <div className="song-card-actions flex flex-col items-center gap-4 flex-shrink-0">
                        {/* Big Play Button */}
                        <button
                            onClick={handlePlayClick}
                            className={`w-16 h-16 rounded-full bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] flex items-center justify-center text-white shadow-lg shadow-red-600/40 transition-transform active:scale-95 hover:bg-red-500 ${
                                isPlaying ? 'animate-pulse' : ''
                            }`}
                            title={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="w-8 h-8"
                                >
                                    <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="w-8 h-8 ml-0.5"
                                >
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            )}
                        </button>
                        {/* Comment Icon */}
                        <button
                            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/90 hover:bg-white/20 transition"
                            title="Comment"
                            onClick={() => {
                                if (!realArtistId) return;
                                navigate(`/messages?to=${realArtistId}`);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 11.5a8.38 8.38 0 0 1-1.9 5.4 8.5 8.5 0 0 1-6.6 3.1 8.38 8.38 0 0 1-5.4-1.9L3 21l1.9-4.1A8.38 8.38 0 0 1 3 11.5 8.5 8.5 0 0 1 6.1 4.9 8.38 8.38 0 0 1 11.5 3h.5a8.48 8.48 0 0 1 9 8.5Z" />
                            </svg>
                        </button>
                        {/* Crown Icon */}
                        <button
                            className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] flex items-center justify-center text-white shadow-lg shadow-red-600/40 hover:bg-red-500 transition"
                            title="Tip"
                            onClick={handleSupportClick}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-5 h-5"
                            >
                                <path d="M5 16L3 5l5.5 3L12 4l3.5 4L21 5l-2 11H5zm2.7-1h8.6l1.2-7.4-3.1 2.4L12 8l-2.4 2-3.1-2.4L7.7 15z" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Recent Supporters Section */}
                <div className="song-card-supporters mt-8 w-full" id={supportersSectionId}>
                    <div className="text-white font-semibold text-lg mb-4">Recent Supporters</div>
                    <div className="space-y-3">
                        {recentTips.length === 0 ? (
                            <div className="text-white/40 text-sm">No supporters yet</div>
                        ) : (
                            recentTips.map((tip) => {
                                const user = tip.supporterUser;
                                const userName = tip.senderName || user?.name || user?.displayName || user?.fullName || tip.fromName || 'Supporter';
                                const userPhoto = user?.photoURL;
                                const badge = tip.badgeInfo || (tip.badgeType ? {
                                    badgeType: tip.badgeType,
                                    level: tip.badgeLevel,
                                    badgeTitle: tip.badgeTitle,
                                    tipCount: tip.badgeTipCount,
                                } : null);
                                const badgeEmoji = badge?.badgeType === 'crown' ? '👑' : 
                                                  badge?.badgeType === 'diamond' ? '💎' : 
                                                  badge?.badgeType === 'gold' ? '🥇' : 
                                                  badge?.badgeType === 'silver' ? '🥈' : 
                                                  badge?.badgeType === 'bronze' ? '🥉' : null;
                                const badgeText = badge ? `${badge.badgeType.charAt(0).toUpperCase() + badge.badgeType.slice(1)} Badge ${artist?.name || 'Artist'}: ${badgeEmoji || ''} ${badge.tipCount || 0}` : null;
                                
                                return (
                                    <div
                                        key={tip.id}
                                        role={tip.senderId ? 'button' : undefined}
                                        tabIndex={tip.senderId ? 0 : undefined}
                                        onClick={() => tip.senderId && navigate(`/profile/${tip.senderId}`)}
                                        onKeyDown={(event) => {
                                            if (tip.senderId && (event.key === 'Enter' || event.key === ' ')) {
                                                event.preventDefault();
                                                navigate(`/profile/${tip.senderId}`);
                                            }
                                        }}
                                        className={`flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10 ${tip.senderId ? 'cursor-pointer hover:bg-white/10 transition' : ''}`}
                                    >
                                        {/* Profile Picture */}
                                        <div className="flex-shrink-0">
                                            {userPhoto ? (
                                                <img 
                                                    src={userPhoto} 
                                                    alt={userName}
                                                    className="w-12 h-12 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center text-white font-semibold text-lg">
                                                    {userName.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="text-white font-semibold text-base truncate">{userName}</div>
                                                {tip.status === 'pending' && (
                                                    <span className="px-2 py-0.5 text-xs font-medium bg-yellow-500/20 text-yellow-300 rounded-full border border-yellow-500/30">
                                                        Pending
                                                    </span>
                                                )}
                                            </div>
                                            
                                            {/* Message with Amount */}
                                            <div className="text-white/90 text-sm mb-1">
                                                {tip.message ? `${tip.message} ` : ''}
                                                <span className="text-white/70">({formatCurrency(tip.amount)})</span>
                                            </div>
                                            
                                            {/* Badge Info */}
                                            {badgeText && (
                                                <div className={`hub-badge hub-badge-${(badge?.badgeType || 'default').toLowerCase()} mt-2 max-w-full text-xs`}>
                                                    {badgeText}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Follow Button */}
                                        {tip.senderId && tip.senderId !== auth.currentUser?.uid && (
                                            <div className="flex-shrink-0" onClick={(event) => event.stopPropagation()}>
                                                <FollowButton targetUid={tip.senderId} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Sticky Wide Tip Button at Bottom - only render when showTipButton is true (for SongDetail page) */}
            {showTipButton && (
                <>
                    <div className="song-card-quick-tips fixed bottom-[10rem] left-0 right-0 z-40 px-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                        <div className="mx-auto grid w-full max-w-lg grid-cols-3 gap-2">
                            {quickTipPresets.map((preset) => (
                                <button
                                    key={`${preset.message}-${preset.amount}`}
                                    type="button"
                                    onClick={() => handleQuickTip(preset)}
                                    className="quick-tip-bubble px-1 text-center text-sm font-bold text-yellow-200 transition active:scale-95"
                                >
                                    <span className="quick-tip-amount" aria-label={`Tip ₹${preset.amount}`}>
                                        ₹{preset.amount}
                                    </span>
                                    <span className="quick-tip-label block">{preset.message}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="song-card-tip-cta fixed bottom-16 left-0 right-0 z-40 px-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                        <div className="w-full max-w-lg mx-auto">
                        <button
                            className="w-full rounded-xl bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] px-4 py-4 text-base font-semibold text-white shadow-[0_8px_30px_rgba(255,0,0,0.35)] ring-1 ring-red-500/40 active:scale-[0.99] transition"
                            onClick={handleSupportClick}
                        >
                            Be the Next Supporter ❤️ Tip Now
                        </button>
                        </div>
                    </div>
                </>
            )}

            {showSupportModal && (
                <TipModal
                    key={`tip-modal-${song.id}-${realArtistId}`}
                    artistId={realArtistId}
                    artistName={artist?.name || artist?.displayName || song.artist || 'Artist'}
                    songId={song.id}
                    autoOpened
                    initialAmount={quickTip?.amount || ''}
                    initialMessage={quickTip?.message || ''}
                    autoPay={Boolean(quickTip)}
                    onClose={() => {
                        setShowSupportModal(false);
                        setQuickTip(null);
                    }}
                />
            )}
        </section>
    );
};

export default SongCard;
